import { FetchError, ofetch, type FetchOptions } from "ofetch"

import { createScopedLogger } from "@/lib/observability"

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
type BackendLogger = ReturnType<typeof createScopedLogger>
type BackendCallMetadata = Record<string, unknown>
const defaultBackendLogger = createScopedLogger("backend")

function getUrlMetadata(url: string) {
  try {
    const parsedUrl = new URL(url)
    return {
      upstreamHost: parsedUrl.host,
      upstreamPath: parsedUrl.pathname,
    }
  } catch {
    return {
      upstreamHost: "invalid-url",
      upstreamPath: "invalid-url",
    }
  }
}

function getFetchErrorStatus(error: unknown) {
  if (error instanceof FetchError) {
    return error.response?.status
  }

  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { status?: unknown } }).response
    return typeof response?.status === "number" ? response.status : undefined
  }

  return undefined
}

function getResponseMetadata(response: Response) {
  return {
    status: response.status,
    ok: response.ok,
    contentType: response.headers.get("Content-Type") ?? undefined,
  }
}

function getBaseMetadata({ method, url, metadata }: { method: HttpMethod; url: string; metadata?: BackendCallMetadata }) {
  return {
    method,
    ...getUrlMetadata(url),
    ...metadata,
  }
}

export async function loggedBackendFetch(
  url: string,
  init: RequestInit,
  {
    logger,
    event,
    metadata,
  }: {
    logger: BackendLogger
    event: string
    metadata?: BackendCallMetadata
  }
) {
  const method = (init.method ?? "GET") as HttpMethod
  const startedAt = Date.now()
  const baseMetadata = getBaseMetadata({ method, url, metadata })

  logger.info(`${event}.started`, baseMetadata)

  try {
    const response = await fetch(url, init)
    const responseMetadata = {
      ...baseMetadata,
      ...getResponseMetadata(response),
      durationMs: Date.now() - startedAt,
    }

    if (response.ok) {
      logger.info(`${event}.succeeded`, responseMetadata)
    } else {
      logger.warn(`${event}.failed_response`, responseMetadata)
    }

    return response
  } catch (error) {
    await logger.error(`${event}.failed`, error, {
      ...baseMetadata,
      durationMs: Date.now() - startedAt,
    })
    throw error
  }
}

export async function loggedBackendOfetch<T>(
  url: string,
  options: FetchOptions,
  {
    logger,
    event,
    metadata,
  }: {
    logger: BackendLogger
    event: string
    metadata?: BackendCallMetadata
  }
) {
  const method = (options.method ?? "GET") as HttpMethod
  const startedAt = Date.now()
  const baseMetadata = getBaseMetadata({ method, url, metadata })

  logger.info(`${event}.started`, baseMetadata)

  try {
    const response = await ofetch.raw<T>(url, options as Parameters<typeof ofetch.raw<T>>[1])
    logger.info(`${event}.succeeded`, {
      ...baseMetadata,
      status: response.status,
      durationMs: Date.now() - startedAt,
    })

    return response._data as T
  } catch (error) {
    await logger.error(`${event}.failed`, error, {
      ...baseMetadata,
      status: getFetchErrorStatus(error),
      durationMs: Date.now() - startedAt,
    })
    throw error
  }
}

export async function loggedBackendOfetchRaw<T>(
  url: string,
  options: FetchOptions,
  {
    logger,
    event,
    metadata,
  }: {
    logger: BackendLogger
    event: string
    metadata?: BackendCallMetadata
  }
) {
  const method = (options.method ?? "GET") as HttpMethod
  const startedAt = Date.now()
  const baseMetadata = getBaseMetadata({ method, url, metadata })

  logger.info(`${event}.started`, baseMetadata)

  try {
    const response = await ofetch.raw<T>(url, options as Parameters<typeof ofetch.raw<T>>[1])
    logger.info(`${event}.succeeded`, {
      ...baseMetadata,
      status: response.status,
      durationMs: Date.now() - startedAt,
    })

    return response
  } catch (error) {
    await logger.error(`${event}.failed`, error, {
      ...baseMetadata,
      status: getFetchErrorStatus(error),
      durationMs: Date.now() - startedAt,
    })
    throw error
  }
}

export function loggedDefaultBackendOfetch<T>(url: string, options: FetchOptions = {}) {
  return loggedBackendOfetch<T>(url, options, {
    logger: defaultBackendLogger,
    event: "backend.upstream_request",
  })
}

loggedDefaultBackendOfetch.raw = function loggedDefaultBackendOfetchRaw<T>(url: string, options: FetchOptions = {}) {
  return loggedBackendOfetchRaw<T>(url, options, {
    logger: defaultBackendLogger,
    event: "backend.upstream_request",
  })
}
